import { NodeDefs, NodeDefType, Objects } from '@openforis/arena-core'

import { uuidv4 } from '@core/uuid'
import * as A from '@core/arena'
import * as ObjectUtils from '@core/objectUtils'
import * as StringUtils from '@core/stringUtils'
import { ArrayUtils } from '@core/arrayUtils'
import { functionNames, userDependentFunctionNames } from '@core/expressionParser/helpers/functions'
import { extractCallFirstTwoArgs } from '@core/expressionParser/helpers/functionCallArgs'

import * as TextUtils from '@webapp/utils/textUtils'

import { nodeDefType } from './nodeDefType'
import * as NodeDefLayout from './nodeDefLayout'
import * as NodeDefValidations from './nodeDefValidations'
import * as NodeDefExpression from './nodeDefExpression'
import { valuePropsTaxon } from './nodeValueProps'

// ======== NODE DEF PROPERTIES

export { nodeDefType }

export const NodeDefLayoutElementTypes = [nodeDefType.formHeader]

export const keys = {
  id: ObjectUtils.keys.id,
  uuid: ObjectUtils.keys.uuid,
  parentUuid: ObjectUtils.keys.parentUuid,
  props: ObjectUtils.keys.props,
  propsAdvanced: 'propsAdvanced',
  propsAdvancedDraft: 'propsAdvancedDraft',
  meta: 'meta',
  draftAdvanced: 'draftAdvanced',
  draftAdvancedApplicable: 'draftAdvancedApplicable',
  draftAdvancedDefaultValues: 'draftAdvancedDefaultValues',
  draftAdvancedFileNameExpression: 'draftAdvancedFileNameExpression',
  draftAdvancedValidations: 'draftAdvancedValidations',
  type: 'type',
  deleted: 'deleted',
  analysis: 'analysis',
  published: 'published',
  temporary: 'temporary', // Not persisted yet
  // Analysis
  virtual: 'virtual', // Virtual Entity
}

export const NodeDefLabelTypes = {
  name: 'name',
  label: 'label',
  labelAndName: 'labelAndName',
}

export const propKeys = {
  cycles: 'cycles',
  descriptions: ObjectUtils.keysProps.descriptions,
  enumerate: 'enumerate', // only for multiple entities
  autoCreateMinCountItems: 'autoCreateMinCountItems', // only for multiple entities
  key: 'key',
  autoIncrementalKey: 'autoIncrementalKey',
  labels: ObjectUtils.keysProps.labels,
  multiple: 'multiple',
  qualifier: 'qualifier',
  name: ObjectUtils.keys.name,
  readOnly: 'readOnly',
  layout: 'layout',
  // available only when readOnly is true
  hidden: 'hidden',
  shownAsHyperlink: 'shownAsHyperlink',

  // Text
  textInputType: 'textInputType',
  textTransform: 'textTransform',

  // Decimal / Integer
  maxNumberDecimalDigits: 'maxNumberDecimalDigits',
  unit: 'unit',

  // Boolean
  labelValue: 'labelValue',

  // Code
  categoryUuid: 'categoryUuid',
  parentCodeDefUuid: 'parentCodeDefUuid',

  // Taxon
  taxonomyUuid: 'taxonomyUuid',
  vernacularNameLabels: 'vernacularNameLabels',
  visibleFields: 'visibleFields',
  vernacularNameSelectionKept: 'vernacularNameSelectionKept',
  vernacularNameAlwaysIncludedIfSingle: 'vernacularNameAlwaysIncludedIfSingle',

  // File
  maxFileSize: 'maxFileSize', // max file size in MB
  fileType: 'fileType',
  geotagInformationShown: 'geotagInformationShown',

  // Time
  includeSeconds: 'includeSeconds',

  // Coordinate
  allowOnlyDeviceCoordinate: 'allowOnlyDeviceCoordinate',
  includeAccuracy: 'includeAccuracy',
  includeAltitude: 'includeAltitude',
  includeAltitudeAccuracy: 'includeAltitudeAccuracy',
  mapMarkerColor: 'mapMarkerColor',

  // Geo
  positionTracking: 'positionTracking',

  // layout elements
  headerColor: 'headerColor',

  // print
  printOrientation: 'printOrientation',
}

const commonAttributePropsKeys = [
  propKeys.cycles,
  propKeys.descriptions,
  propKeys.hidden,
  propKeys.key,
  propKeys.labels,
  propKeys.layout,
  propKeys.multiple,
  propKeys.name,
  propKeys.qualifier,
  propKeys.readOnly,
]

export const textInputTypes = {
  singleLine: 'singleLine',
  multiLine: 'multiLine',
}

export const textTransformValues = {
  [TextUtils.textTransformValues.none]: TextUtils.textTransformValues.none,
  [TextUtils.textTransformValues.lowercase]: TextUtils.textTransformValues.lowercase,
  [TextUtils.textTransformValues.uppercase]: TextUtils.textTransformValues.uppercase,
  [TextUtils.textTransformValues.capitalize]: TextUtils.textTransformValues.capitalize,
}

export const booleanLabelValues = {
  trueFalse: 'trueFalse',
  yesNo: 'yesNo',
}

export const coordinateAdditionalFields = {
  accuracy: 'accuracy',
  altitude: 'altitude',
  altitudeAccuracy: 'altitudeAccuracy',
}

export const fileTypeValues = {
  image: 'image',
  video: 'video',
  audio: 'audio',
  other: 'other',
}

export const keysPropsAdvanced = {
  applicable: 'applicable',
  defaultValues: 'defaultValues',
  defaultValueEvaluatedOneTime: 'defaultValueEvaluatedOneTime',
  editableIf: 'editableIf',
  excludedInClone: 'excludedInClone',
  validations: 'validations',
  visibleIf: 'visibleIf',
  formula: 'formula',

  // Analisys
  script: 'script',
  chainUuid: 'chainUuid',
  index: 'index',
  active: 'active',
  aggregateFunctions: 'aggregateFunctions',

  isBaseUnit: 'isBaseUnit',
  isSampling: 'isSampling',
  hasAreaBasedEstimated: 'hasAreaBasedEstimated',
  areaBasedEstimatedOf: 'areaBasedEstimatedOf', // uuid of area based estimated node def

  // code and taxon
  itemsFilter: 'itemsFilter',
  // file
  fileNameExpression: 'fileNameExpression',
  enumeratingItemsExpression: 'enumeratingItemsExpression',

  // reporting
  hiddenInReport: 'hiddenInReport',
}

const commonAttributePropsAdvancedKeys = [
  keysPropsAdvanced.applicable,
  keysPropsAdvanced.defaultValues,
  keysPropsAdvanced.defaultValueEvaluatedOneTime,
  keysPropsAdvanced.excludedInClone,
  keysPropsAdvanced.validations,
  keysPropsAdvanced.formula,
]

export const metaKeys = {
  h: 'h',
}

export const maxKeyAttributes = 5
const MAX_FILE_SIZE_DEFAULT = 10

export const visibleFieldsDefaultByType = {
  [nodeDefType.taxon]: [valuePropsTaxon.code, valuePropsTaxon.scientificName, valuePropsTaxon.vernacularName],
}

export const createAutoIncrementalKeyDefaultValues = ({ nodeDef, nodeDefParent }) => {
  const nodeDefName = getName(nodeDef)
  const nodeDefParentName = getName(nodeDefParent)
  return [
    NodeDefExpression.createExpression({ expression: '1', applyIf: 'index($context) == 0' }),
    NodeDefExpression.createExpression({
      expression: `Math.max(parent($context).${nodeDefParentName}.${nodeDefName}) + 1`,
    }),
  ]
}

// ==== READ

export const {
  getLabels,
  getParentUuid,
  getProp,
  getProps,
  getPropsDraft,
  getPropsAndPropsDraft,
  getUuid,
  getId,
  isEqual,
  isPublished,
  isTemporary,
  dissocProp,
} = ObjectUtils

export const getType = A.prop(keys.type)
export const getName = (nodeDef) => getProp(propKeys.name, '')(nodeDef)
export const getCycles = getProp(propKeys.cycles, [])

export const isKey = ObjectUtils.isPropTrue(propKeys.key)
export const isQualifier = ObjectUtils.isPropTrue(propKeys.qualifier)
export const isAutoIncrementalKey = ObjectUtils.isPropTrue(propKeys.autoIncrementalKey)
export const isRoot = A.pipe(getParentUuid, A.isNil)
export const isMultiple = ObjectUtils.isPropTrue(propKeys.multiple)
export const isSingle = A.pipe(isMultiple, A.not)

const isType = (type) => (nodeDef) => getType(nodeDef) === type

export const isEntity = isType(nodeDefType.entity)
export const isSingleEntity = (nodeDef) => isEntity(nodeDef) && isSingle(nodeDef)
export const isMultipleEntity = (nodeDef) => isEntity(nodeDef) && isMultiple(nodeDef)
export const isEntityOrMultiple = (nodeDef) => isEntity(nodeDef) || isMultiple(nodeDef)

export const isAttribute = A.pipe(isEntity, A.not)
export const isSingleAttribute = (nodeDef) => isAttribute(nodeDef) && isSingle(nodeDef)
export const isMultipleAttribute = (nodeDef) => isAttribute(nodeDef) && isMultiple(nodeDef)
export const isAttributeComposite = (nodeDef) =>
  [nodeDefType.coordinate, nodeDefType.date, nodeDefType.file, nodeDefType.taxon, nodeDefType.time].includes(
    getType(nodeDef)
  )

export const isBoolean = isType(nodeDefType.boolean)
export const isCode = isType(nodeDefType.code)
export const isCoordinate = isType(nodeDefType.coordinate)
export const isDate = isType(nodeDefType.date)
export const isDecimal = isType(nodeDefType.decimal)
export const isGeo = isType(nodeDefType.geo)
export const isFile = isType(nodeDefType.file)
export const isInteger = isType(nodeDefType.integer)
export const isTaxon = isType(nodeDefType.taxon)
export const isText = isType(nodeDefType.text)
export const isTime = isType(nodeDefType.time)
// layout elments
export const isFormHeader = isType(nodeDefType.formHeader)

export const isReadOnly = getProp(propKeys.readOnly, false)
export const isHidden = getProp(propKeys.hidden, false)

export const getLayout = getProp(propKeys.layout, {})

export const isDeleted = ObjectUtils.isKeyTrue(keys.deleted)

export const getDescriptions = getProp(propKeys.descriptions, {})

export const isEnumerate = ObjectUtils.isPropTrue(propKeys.enumerate)
export const isAutoCreateMinCountItems = ObjectUtils.isPropTrue(propKeys.autoCreateMinCountItems)

// boolean
export const getLabelValue = getProp(propKeys.labelValue, booleanLabelValues.trueFalse)
export const isBooleanLabelYesNo = (nodeDef) =>
  isBoolean(nodeDef) && getProp(propKeys.labelValue, booleanLabelValues.trueFalse)(nodeDef) === booleanLabelValues.yesNo
// code
export const getCategoryUuid = getProp(propKeys.categoryUuid)
// coordinate
export const isAllowOnlyDeviceCoordinate = ObjectUtils.isPropTrue(propKeys.allowOnlyDeviceCoordinate)
export const isAccuracyIncluded = ObjectUtils.isPropTrue(propKeys.includeAccuracy)
export const isAltitudeIncluded = ObjectUtils.isPropTrue(propKeys.includeAltitude)
export const isAltitudeAccuracyIncluded = ObjectUtils.isPropTrue(propKeys.includeAltitudeAccuracy)
export const getMapMarkerColor = getProp(propKeys.mapMarkerColor)
export const getCoordinateAdditionalFields = NodeDefs.getCoordinateAdditionalFields
// decimal / integer
export const getUnit = getProp(propKeys.unit, '')
// decimal
export const getMaxNumberDecimalDigits = (nodeDef) => {
  const decimalDigits = getProp(propKeys.maxNumberDecimalDigits, Number.NaN)(nodeDef)
  return A.isEmpty(decimalDigits) ? Number.NaN : Number(decimalDigits)
}
// file
export const isNumberOfFilesEnabled = isMultiple
export const getMaxFileSize = (nodeDef) => Number(getProp(propKeys.maxFileSize, MAX_FILE_SIZE_DEFAULT)(nodeDef))
export const getFileType = getProp(propKeys.fileType, fileTypeValues.other)
export const isGeotagInformationShown = ObjectUtils.isPropTrue(propKeys.geotagInformationShown)
// geo
export const isPositionTracking = ObjectUtils.isPropTrue(propKeys.positionTracking)
// time
export const isSecondsIncluded = ObjectUtils.isPropTrue(propKeys.includeSeconds)
// taxon
export const getTaxonomyUuid = getProp(propKeys.taxonomyUuid)
export const getVernacularNameLabels = getProp(propKeys.vernacularNameLabels, {})
export const getVernacularNameLabel = (lang) => (nodeDef) => getVernacularNameLabels(nodeDef)[lang]
export const getVisibleFields = (nodeDef) => {
  const visibleFieldsDefault = visibleFieldsDefaultByType[getType(nodeDef)]
  return getProp(propKeys.visibleFields, visibleFieldsDefault)(nodeDef)
}
export const isVernacularNameSelectionKept = ObjectUtils.isPropTrue(propKeys.vernacularNameSelectionKept)
export const isVernacularNameAlwaysIncludedIfSingle = ObjectUtils.isPropTrue(
  propKeys.vernacularNameAlwaysIncludedIfSingle
)
// text
export const getTextInputType = getProp(propKeys.textInputType, textInputTypes.singleLine)
export const getTextTransform = getProp(propKeys.textTransform, textTransformValues.none)
export const getTextTransformFunction = (nodeDef) =>
  TextUtils.transform({ transformFunction: getTextTransform(nodeDef) })

// layout elements

export const getHeaderColor = getProp(propKeys.headerColor)
export const isLayoutElement = isFormHeader

/**
 * Returns the entity printable orientation, if set.
 * @param {!object} nodeDef - Entity node definition.
 * @returns {string|undefined} 'portrait' | 'landscape' | undefined.
 */
export const getPrintOrientation = getProp(propKeys.printOrientation)

// ==== READ meta
export const getMeta = A.propOr({}, keys.meta)
export const getMetaHierarchy = A.pathOr([], [keys.meta, metaKeys.h])

// Utils
export const getLabel = (nodeDef, lang, type = NodeDefLabelTypes.label, defaultToName = true) => {
  let firstPart = ''
  const name = getName(nodeDef)

  if (type === NodeDefLabelTypes.name) {
    firstPart = name
  } else {
    const label = A.path([keys.props, propKeys.labels, lang], nodeDef)

    if (!StringUtils.isBlank(label)) {
      if (type === NodeDefLabelTypes.label) {
        firstPart = label
      } else if (type === NodeDefLabelTypes.labelAndName) {
        firstPart = `${label} [${name}]`
      }
    }
  }
  // default to name
  if (StringUtils.isBlank(firstPart) && defaultToName) {
    firstPart = name
  }

  let suffix = ''
  if (isVirtual(nodeDef)) suffix = ' (V)'
  else if (isAnalysis(nodeDef) && isAttribute(nodeDef)) suffix = ' (C)'

  return firstPart + suffix
}
export const getLabelWithType = ({ nodeDef, lang, type }) => getLabel(nodeDef, lang, type)

export const getDescription = (lang) => (nodeDef) => A.propOr('', lang, getDescriptions(nodeDef))

export const getCycleFirst = A.pipe(getCycles, A.head)

export const isInCycle = (cycle) => (nodeDef) => getCycles(nodeDef).includes(cycle)

export const isAncestorOf = (nodeDefDescendant) => (nodeDef) =>
  A.startsWith([...getMetaHierarchy(nodeDef), getUuid(nodeDef)], getMetaHierarchy(nodeDefDescendant))

export const isDescendantOf = (nodeDefAncestor) => (nodeDef) => {
  const hAncestor = [...getMetaHierarchy(nodeDefAncestor), getUuid(nodeDefAncestor)]
  return A.startsWith(hAncestor, getMetaHierarchy(nodeDef))
}

// Advanced props

export const getPropsAdvanced = A.propOr({}, keys.propsAdvanced)
export const getPropsAdvancedDraft = A.propOr({}, keys.propsAdvancedDraft)

export const getPropAdvanced = (prop, defaultTo = null) =>
  A.pipe(getPropsAdvanced, A.pathOr(defaultTo, prop.split('.')))

export const getPropAdvancedDraft = (prop, defaultTo = null) =>
  A.pipe(getPropsAdvancedDraft, A.pathOr(defaultTo, prop.split('.')))

export const getPropOrDraftAdvanced =
  (prop, defaultTo = null) =>
  (nodeDef) =>
    getPropAdvancedDraft(prop, getPropAdvanced(prop, defaultTo)(nodeDef))(nodeDef)

export const getAllPropsAndAllPropsDraft =
  ({ backup = false }) =>
  (nodeDef) => {
    const { props, propsDraft } = ObjectUtils.getPropsAndPropsDraft({ backup })(nodeDef)
    const propsAdvanced = getPropsAdvanced(nodeDef)
    const propsAdvancedDraft = getPropsAdvancedDraft(nodeDef)
    return {
      props,
      propsDraft,
      propsAdvanced: backup ? propsAdvanced : {},
      propsAdvancedDraft: backup ? propsAdvancedDraft : { ...propsAdvanced, ...propsAdvancedDraft },
    }
  }

export const hasAdvancedPropsDraft = (nodeDef) => A.prop(keys.draftAdvanced, nodeDef) === true
export const hasAdvancedPropsApplicableDraft = (nodeDef) => A.prop(keys.draftAdvancedApplicable, nodeDef) === true
export const hasAdvancedPropsDefaultValuesDraft = (nodeDef) => A.prop(keys.draftAdvancedDefaultValues, nodeDef) === true
export const hasAdvancedPropsFileNameExpressionDraft = (nodeDef) =>
  A.prop(keys.draftAdvancedFileNameExpression, nodeDef) === true
export const hasAdvancedPropsValidationsDraft = (nodeDef) => A.prop(keys.draftAdvancedValidations, nodeDef) === true
// Unlike the flags above (set on the node def row by the server whenever the corresponding key is
// present in propsAdvancedDraft, for a fixed set of keys it tracks), enumeratingItemsExpression and
// itemsFilter are not tracked server-side, so they're checked here instead, directly against
// propsAdvancedDraft. This only works if the node def was fetched with backup: true (see
// fetchSurveyAndNodeDefsBySurveyId) - a plain draft fetch merges propsAdvancedDraft into propsAdvanced
// and discards it, at which point these would always read as unchanged.
export const hasAdvancedPropsEnumeratingItemsExpressionDraft = (nodeDef) =>
  Boolean(getPropAdvancedDraft(keysPropsAdvanced.enumeratingItemsExpression)(nodeDef))
export const hasAdvancedPropsItemsFilterDraft = (nodeDef) =>
  Boolean(getPropAdvancedDraft(keysPropsAdvanced.itemsFilter)(nodeDef))
// Node defs whose value (or, for auto-enumerated entities, their enumerated child nodes) can be
// recalculated on publish - see RecordCheckJob. Requires the node def to have been fetched with
// backup: true (see the two functions above).
// True if the "required if" condition in the draft advanced props differs from the published one.
// Like the checks below, it works only if the node def was fetched with backup: true.
export const hasRequiredIfDraftChanged = (nodeDef) => {
  if (!hasAdvancedPropsValidationsDraft(nodeDef)) return false
  const requiredIfPath = `${keysPropsAdvanced.validations}.${NodeDefValidations.keys.requiredIf}`
  return !A.equals(getPropAdvancedDraft(requiredIfPath, [])(nodeDef), getPropAdvanced(requiredIfPath, [])(nodeDef))
}

export const hasValueAffectingAdvancedPropsDraft = (nodeDef) =>
  hasAdvancedPropsApplicableDraft(nodeDef) ||
  hasAdvancedPropsDefaultValuesDraft(nodeDef) ||
  hasAdvancedPropsFileNameExpressionDraft(nodeDef) ||
  hasAdvancedPropsEnumeratingItemsExpressionDraft(nodeDef) ||
  hasAdvancedPropsItemsFilterDraft(nodeDef)
const isPropAdvanced = (key) => Object.keys(keysPropsAdvanced).includes(key)

export const getDefaultValues = getPropAdvanced(keysPropsAdvanced.defaultValues, [])
export const hasDefaultValues = A.pipe(getDefaultValues, A.isEmpty, A.not)
export const isDefaultValueEvaluatedOneTime = getPropAdvanced(keysPropsAdvanced.defaultValueEvaluatedOneTime, false)

export const getEditableIf = getPropAdvanced(keysPropsAdvanced.editableIf, [])
export const isAlwaysEditable = A.pipe(getEditableIf, A.isEmpty)
export const getVisibleIf = getPropAdvanced(keysPropsAdvanced.visibleIf, [])
export const isAlwaysVisible = A.pipe(getVisibleIf, A.isEmpty)

export const getValidations = getPropAdvanced(keysPropsAdvanced.validations, {})
export const getValidationExpressions = A.pipe(getValidations, NodeDefValidations.getExpressions)
export const hasValidationsDefined = (nodeDef) => {
  const validations = getValidations(nodeDef)
  return (
    NodeDefValidations.isRequired(validations) ||
    NodeDefValidations.isRequiredIfDefined(validations) ||
    NodeDefValidations.isUnique(validations) ||
    !A.isEmpty(NodeDefValidations.getMinCount(validations)) ||
    !A.isEmpty(NodeDefValidations.getMaxCount(validations)) ||
    !A.isEmpty(NodeDefValidations.getExpressions(validations))
  )
}

export const getApplicable = getPropAdvanced(keysPropsAdvanced.applicable, [])

// Flattens an array of NodeDefExpression objects (as stored under defaultValues/applicable/
// validations.expressions/editableIf/visibleIf - each { expression, applyIf, ... }) into a plain array
// of expression strings (both the expression itself and its applyIf, when present).
const _expressionStringsFromNodeDefExpressions = (nodeDefExpressions) =>
  nodeDefExpressions.reduce((acc, nodeDefExpression) => {
    ArrayUtils.addIfNotEmpty(NodeDefExpression.getExpression(nodeDefExpression))(acc)
    ArrayUtils.addIfNotEmpty(NodeDefExpression.getApplyIf(nodeDefExpression))(acc)
    return acc
  }, [])

export const getAllExpressions = (nodeDef) => {
  const nodeDefExpressions = [
    ...getDefaultValues(nodeDef),
    ...getValidationExpressions(nodeDef),
    ...getApplicable(nodeDef),
    ...getEditableIf(nodeDef),
    ...getVisibleIf(nodeDef),
  ]
  const expressions = _expressionStringsFromNodeDefExpressions(nodeDefExpressions)
  ArrayUtils.addIfNotEmpty(getItemsFilter(nodeDef))(expressions)
  ArrayUtils.addIfNotEmpty(getFileNameExpression(nodeDef))(expressions)
  ArrayUtils.addIfNotEmpty(getEnumeratingItemsExpression(nodeDef))(expressions)
  return expressions
}

// Value-affecting-only subset of getAllExpressions: applicable, default values, file name expression,
// enumerating items expression, items filter - the exact same boundary hasValueAffectingAdvancedPropsDraft
// draws between props that can change a node's stored value and ones that don't (e.g. validations,
// editableIf, visibleIf are excluded). Used to find node defs whose value needs recalculating when
// something they read (e.g. a category/taxonomy extra prop) changes - see
// referencesCategoryExtraProp/referencesTaxonomyExtraProp below.
export const getValueAffectingExpressions = (nodeDef) => {
  const expressions = _expressionStringsFromNodeDefExpressions([
    ...getDefaultValues(nodeDef),
    ...getApplicable(nodeDef),
  ])
  ArrayUtils.addIfNotEmpty(getItemsFilter(nodeDef))(expressions)
  ArrayUtils.addIfNotEmpty(getFileNameExpression(nodeDef))(expressions)
  ArrayUtils.addIfNotEmpty(getEnumeratingItemsExpression(nodeDef))(expressions)
  return expressions
}

// Plain expression strings from the node def's validation expressions only (see
// getValidationExpressions) - used by referencesCategoryExtraPropInValidations/
// referencesTaxonomyExtraPropInValidations below.
const getValidationOnlyExpressionStrings = (nodeDef) =>
  _expressionStringsFromNodeDefExpressions(getValidationExpressions(nodeDef))

const userDependentFunctionsRegExp = new RegExp(String.raw`\b(${userDependentFunctionNames.join('|')})\s*\(`)

// Returns true if any of the node def's expressions references a function whose
// result depends on the currently logged in user (e.g. userProp).
export const hasUserDependentExpressions = (nodeDef) =>
  getAllExpressions(nodeDef).some((expression) => userDependentFunctionsRegExp.test(expression))

// True if any of the given expressions calls categoryItemProp(entityName, propName, ...) /
// taxonProp(entityName, propName, ...) - matched by function name - referencing the given
// category/taxonomy name, for one of the given (changed) extra prop names. A non-literal propName
// argument (identifier, nested call...) can't be resolved statically, so it's conservatively treated
// as a potential match rather than ruled out.
const _referencesExtraPropChange = ({ expressions, functionName, entityName, changedPropNames }) =>
  expressions.some((expression) =>
    extractCallFirstTwoArgs(expression, functionName).some(
      ([entityNameArg, propNameArg]) =>
        entityNameArg === entityName && (propNameArg === null || changedPropNames.has(propNameArg))
    )
  )

// True if nodeDef's value could be affected by a change to one of `changedPropNames` on the category
// named `categoryName` - i.e. one of its value-affecting expressions calls
// categoryItemProp(categoryName, propName, ...) with propName in changedPropNames (or unresolvable).
export const referencesCategoryExtraProp =
  ({ categoryName, changedPropNames }) =>
  (nodeDef) =>
    _referencesExtraPropChange({
      expressions: getValueAffectingExpressions(nodeDef),
      functionName: functionNames.categoryItemProp,
      entityName: categoryName,
      changedPropNames,
    })

// Same as referencesCategoryExtraProp, for taxonProp(taxonomyName, propName, ...) calls.
export const referencesTaxonomyExtraProp =
  ({ taxonomyName, changedPropNames }) =>
  (nodeDef) =>
    _referencesExtraPropChange({
      expressions: getValueAffectingExpressions(nodeDef),
      functionName: functionNames.taxonProp,
      entityName: taxonomyName,
      changedPropNames,
    })

// Same as referencesCategoryExtraProp/referencesTaxonomyExtraProp, but scanning only validation
// expressions - used to trigger re-validation (not value recalculation) of node defs that only
// reference a changed extra prop from a validation expression.
export const referencesCategoryExtraPropInValidations =
  ({ categoryName, changedPropNames }) =>
  (nodeDef) =>
    _referencesExtraPropChange({
      expressions: getValidationOnlyExpressionStrings(nodeDef),
      functionName: functionNames.categoryItemProp,
      entityName: categoryName,
      changedPropNames,
    })

export const referencesTaxonomyExtraPropInValidations =
  ({ taxonomyName, changedPropNames }) =>
  (nodeDef) =>
    _referencesExtraPropChange({
      expressions: getValidationOnlyExpressionStrings(nodeDef),
      functionName: functionNames.taxonProp,
      entityName: taxonomyName,
      changedPropNames,
    })

export const isExcludedInClone = getPropAdvanced(keysPropsAdvanced.excludedInClone, false)

// code and taxon
export const getItemsFilter = getPropAdvanced(keysPropsAdvanced.itemsFilter, '')
// file
export const getFileNameExpression = getPropAdvanced(keysPropsAdvanced.fileNameExpression, '')
export const getEnumeratingItemsExpression = getPropAdvanced(keysPropsAdvanced.enumeratingItemsExpression, '')

// Advanced props - Analysis
export const getFormula = getPropAdvanced(keysPropsAdvanced.formula, [])

export const getParentCodeDefUuid = getProp(propKeys.parentCodeDefUuid)

export const getChainUuid = getPropOrDraftAdvanced(keysPropsAdvanced.chainUuid, null)

export const getChainIndex = getPropOrDraftAdvanced(keysPropsAdvanced.index, 0)

export const isActive = getPropAdvanced(keysPropsAdvanced.active, false)
export const getScript = getPropOrDraftAdvanced(keysPropsAdvanced.script, '')

export const getAggregateFunctions = getPropOrDraftAdvanced(keysPropsAdvanced.aggregateFunctions, {})
export const getAggregateFunctionByUuid = (uuid) => A.pipe(getAggregateFunctions, A.propOr(null, uuid))

// READ Analysis
export const isAnalysis = ObjectUtils.isKeyTrue(keys.analysis)
export const isVirtual = ObjectUtils.isKeyTrue(keys.virtual)
export const isReadOnlyOrAnalysis = (nodeDef) => isReadOnly(nodeDef) || isAnalysis(nodeDef)

export const isBaseUnit = (nodeDef) => Boolean(getPropOrDraftAdvanced(keysPropsAdvanced.isBaseUnit, false)(nodeDef))
export const isSampling = (nodeDef) => Boolean(getPropOrDraftAdvanced(keysPropsAdvanced.isSampling, false)(nodeDef))
export const hasAreaBasedEstimated = (nodeDef) =>
  Boolean(getPropOrDraftAdvanced(keysPropsAdvanced.hasAreaBasedEstimated, false)(nodeDef))
export const getAreaBasedEstimatedOf = getPropOrDraftAdvanced(keysPropsAdvanced.areaBasedEstimatedOf, null)
export const isAreaBasedEstimatedOf = (nodeDef) => Boolean(getAreaBasedEstimatedOf(nodeDef))

// Reporting
export const isHiddenInReport = getPropAdvanced(keysPropsAdvanced.hiddenInReport, false)

// ==== CREATE

export const newNodeDef = (
  nodeDefParent,
  type,
  cycles,
  props,
  propsAdvanced = {},
  analysis = false,
  virtual = false
) => ({
  [keys.uuid]: uuidv4(),
  [keys.parentUuid]: getUuid(nodeDefParent),
  [keys.type]: type,
  [keys.props]: {
    ...props,
    [propKeys.cycles]: cycles,
  },
  [keys.propsAdvanced]: {
    ...propsAdvanced,
  },
  [keys.meta]: {
    [metaKeys.h]: nodeDefParent ? [...getMetaHierarchy(nodeDefParent), getUuid(nodeDefParent)] : [],
  },
  [keys.analysis]: analysis,
  [keys.virtual]: virtual,
  [keys.temporary]: true,
})

// ==== UPDATE

export const assocDeleted = A.assoc(keys.deleted)

export const assocParentUuid = A.assoc(keys.parentUuid)
export const assocMetaHierarchy = A.assocPath([keys.meta, metaKeys.h])
export const { mergeProps } = ObjectUtils
const assocPropsAdvanced = A.assoc(keys.propsAdvanced)
export const mergePropsAdvanced = (propsAdvanced) => (nodeDef) =>
  A.pipe(getPropsAdvanced, A.mergeLeft(propsAdvanced), (propsAdvancedUpdated) =>
    assocPropsAdvanced(propsAdvancedUpdated, nodeDef)
  )(nodeDef)
export const assocDefaultValues = (defaultValues) =>
  mergePropsAdvanced({ [keysPropsAdvanced.defaultValues]: defaultValues })
export const assocDefaultValueEvaluatedOnlyOneTime = (evaluatedOnlyOneTime) =>
  mergePropsAdvanced({ [keysPropsAdvanced.defaultValueEvaluatedOneTime]: evaluatedOnlyOneTime })
export const assocValidations = (validations) => mergePropsAdvanced({ [keysPropsAdvanced.validations]: validations })
export const dissocTemporary = A.dissoc(keys.temporary)
export const assocProp = ({ key, value }) =>
  isPropAdvanced(key) ? mergePropsAdvanced({ [key]: value }) : mergeProps({ [key]: value })
export const assocCycles = (cycles) => assocProp({ key: propKeys.cycles, value: cycles })
export const assocExcludedInClone = (value) => assocProp({ key: keysPropsAdvanced.excludedInClone, value })
export const assocLabels = (labels) => assocProp({ key: propKeys.labels, value: labels })
export const assocLabel =
  ({ label, lang }) =>
  (nodeDef) =>
    assocLabels({ ...getLabels(nodeDef), [lang]: label })(nodeDef)

export const assocDescriptions = (descriptions) => assocProp({ key: propKeys.descriptions, value: descriptions })
export const assocHidden = (hidden) => assocProp({ key: propKeys.hidden, value: hidden })

export const dissocEnumerate = ObjectUtils.dissocProp(propKeys.enumerate)
export const cloneIntoEntityDef =
  ({
    nodeDefParent,
    clonedNodeDefName,
    ignoreDefaultValues = true,
    ignoreApplicability = true,
    ignoreValidations = true,
  }) =>
  (nodeDef) => {
    const propsCloned = ObjectUtils.clone(getProps(nodeDef))
    propsCloned[propKeys.name] = clonedNodeDefName

    const propsAdvancedCloned = ObjectUtils.clone(getPropsAdvanced(nodeDef))
    if (ignoreDefaultValues) {
      delete propsCloned[propKeys.readOnly]
      delete propsAdvancedCloned[keysPropsAdvanced.defaultValues]
      delete propsAdvancedCloned[keysPropsAdvanced.defaultValueEvaluatedOneTime]
      delete propsAdvancedCloned[keysPropsAdvanced.fileNameExpression]
      delete propsCloned[propKeys.autoIncrementalKey]
    }
    if (ignoreApplicability) {
      delete propsAdvancedCloned[keysPropsAdvanced.applicable]
    }
    if (ignoreValidations) {
      delete propsAdvancedCloned[keysPropsAdvanced.validations]
    }
    return newNodeDef(
      nodeDefParent,
      getType(nodeDef),
      [...getCycles(nodeDef)],
      propsCloned,
      propsAdvancedCloned,
      isAnalysis(nodeDef)
    )
  }

export const changeParentEntity =
  ({ targetParentNodeDef }) =>
  (nodeDef) => {
    const targetParentNodeDefUuid = getUuid(targetParentNodeDef)
    return {
      ...nodeDef,
      [keys.parentUuid]: targetParentNodeDefUuid,
      [keys.meta]: {
        ...getMeta(nodeDef),
        [metaKeys.h]: [...getMetaHierarchy(targetParentNodeDef), targetParentNodeDefUuid],
      },
    }
  }

export const convertToType =
  ({ toType }) =>
  (nodeDef) => {
    const propsUpdated = A.pick(commonAttributePropsKeys)(getProps(nodeDef))
    const propsAdvancedToKeep = isAutoIncrementalKey(nodeDef)
      ? commonAttributePropsAdvancedKeys.filter((prop) => prop !== keysPropsAdvanced.defaultValues)
      : commonAttributePropsAdvancedKeys
    const propsAdvancedUpdated = A.pick(propsAdvancedToKeep)(getPropsAdvanced(nodeDef))

    const layout = getLayout(nodeDef)
    const layoutUpdated = Object.entries(layout).reduce((acc, [cycleKey, cycleLayout]) => {
      acc[cycleKey] = A.pick(NodeDefLayout.commonAttributeKeys)(cycleLayout)
      return acc
    }, {})
    propsUpdated[propKeys.layout] = layoutUpdated

    const nodeDefUpdated = {
      ...nodeDef,
      [keys.type]: toType,
      [keys.props]: propsUpdated,
      [keys.propsAdvanced]: propsAdvancedUpdated,
    }
    return nodeDefUpdated
  }

// layout
export const assocLayout = (layout) => ObjectUtils.setProp(propKeys.layout, layout)

export const updateLayout = (updateFn) => (nodeDef) => {
  const layout = getLayout(nodeDef)
  const layoutUpdated = updateFn(layout)
  return assocLayout(layoutUpdated)(nodeDef)
}

export const updateLayoutProp = ({ cycle, prop, value }) =>
  updateLayout(NodeDefLayout.assocLayoutProp(cycle, prop, value))

export const dissocLayoutProp = ({ cycle, prop }) => updateLayout(NodeDefLayout.dissocLayoutProp(cycle, prop))

export const copyLayout =
  ({ cycleFrom, cyclesTo }) =>
  (nodeDef) =>
    updateLayout((layout) => {
      const layoutCycle = NodeDefLayout.getLayoutCycle(cycleFrom)(nodeDef)
      const layoutUpdated = cyclesTo
        .filter((cycleKey) => cycleKey !== cycleFrom)
        .reduce((layoutAcc, cycleKey) => NodeDefLayout.assocLayoutCycle(cycleKey, layoutCycle)(layoutAcc), layout)
      return layoutUpdated
    })(nodeDef)

/**
 * It keeps only cycleToKeep information and moves it to cycleTarget.
 * (it does side effect on the specified nodeDef).
 * @param {!object} params - The function parameters.
 * @param {!string} params.cycleToKeep - Id of source cycle to keep.
 * @param {!string} params.cycleTarget - Id of target cycle.
 * @returns {object} - Returns the nodeDef specified in parameters (it does side effect).
 */
export const keepOnlyOneCycle =
  ({ cycleToKeep, cycleTarget }) =>
  (nodeDef) => {
    nodeDef.props[propKeys.cycles] = [cycleTarget]
    const nodeDefProps = nodeDef[keys.props]
    const layoutForCycle = nodeDefProps[propKeys.layout]?.[cycleToKeep]

    if (Objects.isEmpty(layoutForCycle)) {
      delete nodeDefProps[propKeys.layout]
    } else {
      nodeDefProps[propKeys.layout] = { [cycleTarget]: layoutForCycle }
    }
    return nodeDef
  }

// ==== UTILS
export const canNodeDefBeMultiple = (nodeDef) =>
  // Entity def but not root
  (isEntity(nodeDef) && !isRoot(nodeDef)) ||
  // Attribute def but not layout element and not analysis
  (!isLayoutElement(nodeDef) &&
    !isAnalysis(nodeDef) &&
    A.includes(getType(nodeDef), [
      nodeDefType.decimal,
      nodeDefType.code,
      nodeDefType.file,
      nodeDefType.integer,
      nodeDefType.text,
    ]))

export const canNodeDefTypeBeKey = (type) =>
  A.includes(type, [
    nodeDefType.date,
    nodeDefType.decimal,
    nodeDefType.code,
    nodeDefType.integer,
    nodeDefType.taxon,
    nodeDefType.text,
    nodeDefType.time,
  ])

export const canNodeDefBeKey = (nodeDef) =>
  !isLayoutElement(nodeDef) && !isAnalysis(nodeDef) && canNodeDefTypeBeKey(getType(nodeDef))

export const canHaveDefaultValue = (nodeDef) =>
  isSingleAttribute(nodeDef) &&
  A.includes(getType(nodeDef), [
    nodeDefType.boolean,
    nodeDefType.code,
    nodeDefType.coordinate,
    nodeDefType.date,
    nodeDefType.decimal,
    nodeDefType.geo,
    nodeDefType.integer,
    nodeDefType.taxon,
    nodeDefType.text,
    nodeDefType.time,
  ])

export const belongsToAllCycles = (cycles) => (nodeDef) => A.isEmpty(A.difference(cycles, getCycles(nodeDef)))
export const canBeExcludedInClone = (nodeDef) => !isRoot(nodeDef) && !isKey(nodeDef)

const isEntityAndNotRoot = (nodeDef) => isEntity(nodeDef) && !isRoot(nodeDef)
export const isDisplayAsEnabled = isEntityAndNotRoot
export const isDisplayInEnabled = isEntityAndNotRoot

export const canMultipleAttributeBeAggregated = (nodeDef) =>
  [nodeDefType.code, nodeDefType.decimal, nodeDefType.integer, nodeDefType.text].includes(getType(nodeDef))

export const canNameBeEdited = (nodeDef) => !isSampling(nodeDef)
export const canBeHidden = (nodeDef) => isReadOnly(nodeDef)
export const canBeShownAsHyperlinkOrMarkdown = (nodeDef) => isText(nodeDef) && isReadOnly(nodeDef)
export const canBeHiddenInMobile = (nodeDef) =>
  !isKey(nodeDef) &&
  !NodeDefValidations.isRequired(getValidations(nodeDef)) &&
  !NodeDefValidations.isRequiredIfDefined(getValidations(nodeDef))

export const canIncludeInMultipleEntitySummary = (cycle) => (nodeDef) =>
  isAttribute(nodeDef) &&
  !isLayoutElement(nodeDef) &&
  !isKey(nodeDef) &&
  !isMultiple(nodeDef) &&
  !isFile(nodeDef) &&
  !isGeo(nodeDef) &&
  !isAnalysis(nodeDef) &&
  NodeDefLayout.canIncludeInMultipleEntitySummary(cycle)(nodeDef)

export const canIncludeInPreviousCycleLink = (cycle) => (nodeDef) =>
  !isKey(nodeDef) && NodeDefLayout.canIncludeInPreviousCycleLink(cycle)(nodeDef)

export const canHaveMobileProps = (cycle) => (nodeDef) =>
  canBeHiddenInMobile(nodeDef) || canIncludeInPreviousCycleLink(cycle)(nodeDef)

export const canHaveAutoIncrementalKey = ({ nodeDef, nodeDefParent }) => {
  if (!isKey(nodeDef) || !isInteger(nodeDef)) return false

  const defaultValues = getDefaultValues(nodeDef)
  if (defaultValues.length === 0) return true

  const autoIncrementalDefaultValues = createAutoIncrementalKeyDefaultValues({ nodeDef, nodeDefParent })
  return (
    defaultValues.length === autoIncrementalDefaultValues.length &&
    autoIncrementalDefaultValues.every((defaultValue, index) =>
      NodeDefExpression.isSimilarTo(defaultValue)(defaultValues[index])
    )
  )
}

export const canShowGeotagInformation = (nodeDef) => getFileType(nodeDef) === fileTypeValues.image

// Arena Mobile records the track only in single geo attributes
export const canBePositionTracking = (nodeDef) => isGeo(nodeDef) && isSingle(nodeDef)

export const canBeHiddenInReport = (nodeDef) =>
  [NodeDefType.boolean, NodeDefType.code, NodeDefType.taxon].includes(getType(nodeDef))

export const canHaveAutoCreateMinCountItems = (nodeDef) => isMultipleEntity(nodeDef) && !isEnumerate(nodeDef)

export const clearNotApplicableProps = (cycle) => (nodeDef) => {
  let nodeDefUpdated = nodeDef
  if (!canHaveAutoCreateMinCountItems(nodeDefUpdated) && isAutoCreateMinCountItems(nodeDefUpdated)) {
    nodeDefUpdated = assocProp({ key: propKeys.autoCreateMinCountItems, value: false })(nodeDefUpdated)
  }
  // clear hidden if not applicable
  if (!canBeHidden(nodeDefUpdated) && isHidden(nodeDefUpdated)) {
    nodeDefUpdated = assocHidden(false)(nodeDefUpdated)
  }
  // clear hidden in mobile if not applicable
  if (!canBeHiddenInMobile(nodeDefUpdated) && NodeDefLayout.isHiddenInMobile(cycle)(nodeDef)) {
    nodeDefUpdated = dissocLayoutProp({ cycle, prop: NodeDefLayout.keys.hiddenInMobile })(nodeDefUpdated)
  }
  // text attribute: clear render type hyperlink or Markdown if not applicable
  if (
    isText(nodeDef) &&
    !canBeShownAsHyperlinkOrMarkdown(nodeDefUpdated) &&
    NodeDefLayout.getRenderType(cycle)(nodeDef) !== NodeDefLayout.textRenderType.text
  ) {
    nodeDefUpdated = dissocLayoutProp({ cycle, prop: NodeDefLayout.keys.renderType })(nodeDefUpdated)
  }
  // clear include in multiple entity summary if not applicable
  if (
    !canIncludeInMultipleEntitySummary(cycle)(nodeDefUpdated) &&
    NodeDefLayout.isIncludedInMultipleEntitySummary(cycle)(nodeDefUpdated)
  ) {
    nodeDefUpdated = dissocLayoutProp({ cycle, prop: NodeDefLayout.keys.includedInMultipleEntitySummary })(
      nodeDefUpdated
    )
  }
  // clear include in previous cycle link if not applicable
  if (
    !canIncludeInPreviousCycleLink(cycle)(nodeDefUpdated) &&
    NodeDefLayout.isIncludedInPreviousCycleLink(cycle)(nodeDefUpdated)
  ) {
    nodeDefUpdated = dissocLayoutProp({ cycle, prop: NodeDefLayout.keys.includedInPreviousCycleLink })(nodeDefUpdated)
  }
  if (!canBeExcludedInClone(nodeDefUpdated) && isExcludedInClone(nodeDefUpdated)) {
    nodeDefUpdated = assocExcludedInClone(false)(nodeDefUpdated)
  }
  if (!canShowGeotagInformation(nodeDefUpdated) && isGeotagInformationShown(nodeDefUpdated)) {
    nodeDefUpdated = ObjectUtils.setProp(propKeys.geotagInformationShown, false)(nodeDefUpdated)
  }
  if (!canBePositionTracking(nodeDefUpdated) && isPositionTracking(nodeDefUpdated)) {
    nodeDefUpdated = ObjectUtils.setProp(propKeys.positionTracking, false)(nodeDefUpdated)
  }
  if (!canBeHiddenInReport(nodeDefUpdated) && isHiddenInReport(nodeDefUpdated)) {
    nodeDefUpdated = assocProp({ key: keysPropsAdvanced.hiddenInReport, value: false })(nodeDefUpdated)
  }
  if (NodeDefLayout.isHiddenWhenNotRelevant(cycle)(nodeDefUpdated) && A.isEmpty(getApplicable(nodeDefUpdated))) {
    nodeDefUpdated = dissocLayoutProp({ cycle, prop: NodeDefLayout.keys.hiddenWhenNotRelevant })(nodeDefUpdated)
  }
  if (isDefaultValueEvaluatedOneTime(nodeDefUpdated) && A.isEmpty(getDefaultValues(nodeDefUpdated))) {
    nodeDefUpdated = assocDefaultValueEvaluatedOnlyOneTime(false)(nodeDefUpdated)
  }
  return nodeDefUpdated
}
